import React, { useEffect, useRef, useState } from 'react';
import Input from '../../../components/Inputs/Input/Input';
import findByVin from '../../../api/cars/findByVin';
import { carSummary, initialIndex, isSameVin, nextIndex, vinSearchQuery } from './vinSearch';
import formatNumber from '../../../utils/formatNumber';

const SEARCH_DELAY_MS = 350;

// Подсказка плавает поверх следующих полей: внутри ячейки сетки она раздвигала бы строку и сбивала выравнивание.
const box = {
  position: 'absolute',
  top: '100%',
  left: 0,
  zIndex: 30,
  width: 'max(100%, 400px)',
  marginTop: 6,
  padding: 4,
  background: '#fff',
  border: '1px solid #E3E3E3',
  borderRadius: 10,
  boxShadow: '0 8px 24px rgba(5, 20, 31, 0.14)',
};
const row = {
  display: 'flex',
  gap: 12,
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 10px',
  borderRadius: 8,
};
const pick = {
  flexShrink: 0,
  padding: '4px 12px',
  fontSize: 13,
  color: 'var(--brand-primary)',
  background: 'transparent',
  border: '1px solid var(--brand-primary)',
  borderRadius: 6,
  cursor: 'pointer',
};

const describe = (car) => [carSummary(car), car.price ? `${formatNumber(car.price)} ₸` : ''].filter(Boolean).join(' · ');

/**
 * Поле VIN с поиском уже заведённых машин: пока менеджер вводит номер, ниже появляется список совпадений
 * (по любой части VIN, от пяти знаков) с кнопкой «Выбрать» — вторую карточку на ту же машину заводить не нужно.
 * С клавиатуры: ↑/↓ двигают подсветку, Enter выбирает подсвеченную (сразу — полное совпадение или первую),
 * Esc закрывает; клик мимо закрывает тоже, дальнейший ввод открывает список снова.
 */
const VinInput = ({ onPickCar, onSearch, component, ...props }) => {
  const [cars, setCars] = useState([]);
  const [closed, setClosed] = useState(false);
  // Запрос в базу идёт: пока ждём ответ, показываем «Ищем…», а не пустоту.
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const wrapper = useRef(null);
  const query = vinSearchQuery(props.value);
  // Родителю полезно знать итог поиска: по нему экран сделки решает, показывать ли форму нового авто.
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;
  const report = (state) => onSearchRef.current && onSearchRef.current({ query, cars: [], done: false, ...state });

  useEffect(() => setClosed(false), [query]);

  useEffect(() => {
    if (!query) {
      setCars([]);
      setLoading(false);
      report({ query: null });
      return undefined;
    }
    // Прежний список относился к другому началу номера — убираем, чтобы не выбрать не то.
    setCars([]);
    setLoading(true);
    report({});
    let cancelled = false;
    const timer = setTimeout(() => {
      findByVin(query)
        .then((res) => {
          const list = Array.isArray(res?.data) ? res.data : [];
          if (cancelled) return;
          setCars(list);
          setLoading(false);
          setActive(initialIndex(list, props.value));
          report({ cars: list, done: true });
        })
        .catch(() => {
          if (cancelled) return;
          setCars([]);
          setLoading(false);
          report({ done: true, failed: true });
        });
    }, SEARCH_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  const visible = (cars.length > 0 || loading) && !closed;
  useEffect(() => {
    if (!visible) return undefined;
    const onDown = (event) => {
      if (wrapper.current && !wrapper.current.contains(event.target)) setClosed(true);
    };
    const onKey = (event) => event.key === 'Escape' && setClosed(true);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [visible]);

  const onKeyDown = (event) => {
    if (!visible) return;
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((index) => nextIndex(index, event.key === 'ArrowDown' ? 1 : -1, cars.length));
    } else if (event.key === 'Enter') {
      // Enter в поле VIN не должен отправлять форму вокруг — он выбирает подсвеченную машину.
      event.preventDefault();
      if (cars[active] && onPickCar) onPickCar(cars[active]);
    }
  };

  return (
    <div style={{ position: 'relative' }} ref={wrapper} onKeyDown={onKeyDown}>
      <Input {...props} />
      {visible && (
        <div style={box} role="listbox">
          {loading && (
            <div style={{ ...row, color: '#8B8B8B', fontSize: 13, justifyContent: 'flex-start' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="12" r="9" fill="none" stroke="#D9D9D9" strokeWidth="3" />
                <path d="M12 3a9 9 0 0 1 9 9" fill="none" stroke="var(--brand-primary)" strokeWidth="3" strokeLinecap="round">
                  <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.8s" repeatCount="indefinite" />
                </path>
              </svg>
              Ищем в базе…
            </div>
          )}
          {cars.map((car, index) => (
            <div
              key={car.id}
              role="option"
              aria-selected={index === active}
              style={{
                ...row,
                // Подсвеченная стрелками или мышью; полное совпадение VIN — зелёным: машина точно уже заведена.
                background: index === active ? '#F0E9DF' : isSameVin(car, props.value) ? '#EAF5EC' : 'transparent',
              }}
              onMouseEnter={() => setActive(index)}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600, letterSpacing: 0.3 }}>{car.vin}</div>
                <div style={{ fontSize: 12, color: '#8B8B8B', marginTop: 2 }}>{describe(car) || 'без описания'}</div>
              </div>
              <button type="button" style={pick} onClick={() => onPickCar && onPickCar(car)}>
                Выбрать
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default VinInput;
